import qs from 'qs';
import { CreateBibleStudyDto, PutBibleStudyDto } from './models/BibleStudy';
import { CreateMissionStatement, MissionStatement } from './models/MissionStatement';
import { RhEvent } from './models/RhEvent';
import { Church, CreateChurch } from './models/Church';

export type APIRequestConfig = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url: string;
  data?: object | File;
  upload?: boolean;
};

export type BibleRangePayload = {
  book: string;
  version?: string;
  fromChapter: number;
  fromVerse: number;
  toChapter: number;
  toVerse: number;
};

export type UserUpdatePayload = {
  email: string;
  externalId: string;
  name: string;
  password?: string;
  imagePath: string;
  role: string;
  churchId: string;
};

export type NotificationPayload = {
  title: string;
  content: string;
  notifyDateTime: Date;
  category?: string;
  type?: string;
  image?: string;
  targetUsers?: string[];
};

export type StudyVisibilityPayload = {
  IsAllStudyVisible: boolean;
};

/**
 * List of api request configs to be fed into the useRequest hook
 */
export const apiConfigs = {
  getBibleStudies: (params?: {
    isFeatured?: boolean;
    churchId?: string;
    showOnlyFromCurrentUser?: boolean;
    limit?: number;
    offset?: number;
    name?: string;
  }): APIRequestConfig => {
    const search = params && Object.keys(params).length ? qs.stringify(params) : '';
    return {
      method: 'GET',
      url: search ? `/programs?${search}` : '/programs',
    };
  },
  getBibleStudy: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/programs/${id}`,
  }),
  addBibleStudy: (payload: CreateBibleStudyDto): APIRequestConfig => ({
    method: 'POST',
    url: '/programs',
    data: payload,
  }),
  putBibleStudy: (payload: PutBibleStudyDto): APIRequestConfig => ({
    method: 'PUT',
    url: `/programs/${payload.id}`,
    data: payload,
  }),
  getChurches: (): APIRequestConfig => ({
    method: 'GET',
    url: '/churches',
  }),
  uploadImage: (src: File): APIRequestConfig => ({
    method: 'POST',
    url: '/media/images',
    upload: true,
    data: src,
  }),
  uploadVideo: (src: File): APIRequestConfig => ({
    method: 'POST',
    url: '/media/videos',
    upload: true,
    data: src,
  }),
  uploadDocument: (src: File): APIRequestConfig => ({
    method: 'POST',
    url: '/media/documents',
    upload: true,
    data: src,
  }),
  getStudyVisibility: (): APIRequestConfig => ({
    method: 'GET',
    url: '/study-visibility',
  }),
  putStudyVisibility: (payload: StudyVisibilityPayload): APIRequestConfig => ({
    method: 'PUT',
    url: '/study-visibility',
    data: payload,
  }),
  getEvents: (params?: {
    isFeatured?: boolean;
    showOnlyFromCurrentUser?: boolean;
    showEventsFromCurrentUser?: boolean;
    limit?: number;
    offset?: number;
    upcomingEvents?: boolean;
    pastEvents?: boolean;
    /** Optional server-side search term (backend should implement). */
    name?: string;
  }): APIRequestConfig => {
    const search = qs.stringify(params);
    return {
      method: 'GET',
      url: `/events?${search}`,
    };
  },
  getUpcomingEvents: (): APIRequestConfig => ({
    method: 'GET',
    url: '/events?UpcomingEvents=true',
  }),
  addEvent: (
    payload: Omit<
      RhEvent,
      | 'id'
      | 'churchId'
      | 'status'
      | 'startTime'
      | 'endTime'
      | 'dateUpdated'
      | 'dateCreated'
      | 'leader'
      | 'theProgram'
      | 'muxPlaybackID'
      | 'numberOfStudiers'
    > & {
      startTime: Date;
      endTime: Date;
    }
  ): APIRequestConfig => ({
    method: 'POST',
    url: '/events',
    data: payload,
  }),
  getEventLeaders: (): APIRequestConfig => ({
    method: 'GET',
    url: '/eventleaders',
  }),
  getEvent: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/events/${id}`,
  }),
  getEventLeader: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/eventleaders/${id}`,
  }),
  getBibleBooks: (bibleId: string): APIRequestConfig => ({
    method: 'GET',
    url: `/bible/${bibleId}/books`,
  }),
  getBibleBookStructure: (bibleId: string, bookId: string): APIRequestConfig => ({
    method: 'GET',
    url: `/bible/${bibleId}/${bookId}/structure`,
  }),
  getBibleRange: (payload: BibleRangePayload): APIRequestConfig => {
    const { book, fromChapter, fromVerse, toChapter, toVerse, version } = payload;
    return {
      method: 'GET',
      url: `/bible?scripture=${book}&scripture=${fromChapter}&scripture=${fromVerse}&scripture=-&scripture=${toChapter}&scripture=${toVerse}&translationId=${version}`,
    };
  },
  getBibleVersions: (): APIRequestConfig => ({
    method: 'GET',
    url: '/bibles/versions',
  }),
  deleteEvent: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/events/${id}`,
  }),
  putEvent: (payload: RhEvent): APIRequestConfig => ({
    method: 'PUT',
    url: `/events/${payload.id}`,
    data: payload,
  }),
  startEvent: (eventId: string): APIRequestConfig => ({
    method: 'PUT',
    url: `/events/${eventId}/start`,
  }),
  endEvent: (eventId: string): APIRequestConfig => ({
    method: 'PUT',
    url: `/events/${eventId}/end`,
  }),
  uploadAudio: (src: File): APIRequestConfig => ({
    method: 'POST',
    url: '/media/audio',
    upload: true,
    data: src,
  }),
  inviteUser: (payload: { email: string; role: string; churchId?: string }): APIRequestConfig => ({
    method: 'POST',
    url: '/User/invite',
    data: payload,
  }),
  deleteUser: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/User/${id}/remove`,
  }),
  getRoles: (): APIRequestConfig => ({
    method: 'GET',
    url: '/user/roles',
  }),
  deleteProgram: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/programs/${id}`,
  }),
  getUsers: (): APIRequestConfig => ({
    method: 'GET',
    url: '/users',
  }),
  getUserById: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/user/${id}`,
  }),
  updateUserById: (id: string, payload: UserUpdatePayload): APIRequestConfig => ({
    method: 'PUT',
    url: `/User/${id}/update`,
    data: payload,
  }),
  getNotifications: (params?: {
    type?: 'schedule' | 'all' | 'past';
    limit?: number;
    offset?: number;
  }): APIRequestConfig => {
    const search = qs.stringify(params);
    return {
      method: 'GET',
      url: search ? `/notifications?${search}` : '/notifications',
    };
  },
  addNotification: (payload: NotificationPayload): APIRequestConfig => ({
    method: 'POST',
    url: '/notification',
    data: payload,
  }),
  deleteNotification: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/notification/${id}`,
  }),
  updateNotification: (id: string, payload: Omit<NotificationPayload, 'targetUsers'>): APIRequestConfig => ({
    method: 'PUT',
    url: `/notification/${id}`,
    data: payload,
  }),
  addMissionStatement: (payload: CreateMissionStatement): APIRequestConfig => ({
    method: 'POST',
    url: `/missionstatements`,
    data: { ...payload, status: 'published' },
  }),
  getMissionStatements: (): APIRequestConfig => ({
    method: 'GET',
    url: `/missionstatements`,
  }),
  updateMissionStatement: (id: string, payload: Partial<MissionStatement>): APIRequestConfig => ({
    method: 'PUT',
    url: `/missionstatements/${id}`,
    data: { ...payload, status: 'published' },
  }),
  deleteMissionStatement: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/missionstatements/${id}`,
  }),
  getMissionStatementById: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/missionstatements/${id}`,
  }),

  addChurch: (payload: CreateChurch): APIRequestConfig => ({
    method: 'POST',
    url: `/churches`,
    data: { ...payload },
  }),
  updateChurch: (id: string, payload: Partial<Church>): APIRequestConfig => ({
    method: 'PUT',
    url: `/churches`,
    data: { ...payload },
  }),
  deleteChurch: (id: string): APIRequestConfig => ({
    method: 'DELETE',
    url: `/churches/${id}`,
  }),
  getChurchById: (id: string): APIRequestConfig => ({
    method: 'GET',
    url: `/churches/${id}/byid`,
  }),

  getPaymentsSummary: (churchNameQuery?: string): APIRequestConfig => ({
    method: 'GET',
    url: churchNameQuery
      ? `/payment/summary?churchNameQuery=${encodeURIComponent(churchNameQuery)}`
      : `/payment/summary`,
  }),

  getPaymentsByChurch: (
    churchId: string,
    options?: {
      dateFrom?: string;
      dateTo?: string;
      statuses?: string;
      emailId?: string;
    }
  ): APIRequestConfig => {
    const params = new URLSearchParams();
    params.append('churchId', churchId);
    if (options?.dateFrom) {
      params.append('dateFrom', options.dateFrom);
    }
    if (options?.dateTo) {
      params.append('dateTo', options.dateTo);
    }
    if (options?.statuses) {
      params.append('statuses', options.statuses);
    }
    if (options?.emailId) {
      params.append('EmailId', options.emailId);
    }
    return {
      method: 'GET',
      url: `/payment/church?${params.toString()}`,
    };
  },

  getUserRequestsPending: (): APIRequestConfig => ({
    method: 'GET',
    url: `/user-request/pending`,
  }),

  processUserRequest: (id: string, status: 'approved' | 'rejected', comments: string): APIRequestConfig => ({
    method: 'POST',
    url: `/user-request/${id}/process`,
    data: {
      Status: status,
      Comments: comments,
    },
  }),
};
